import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery, {
  IBaseMutationResponse
} from "../../../../utils/customBaseQuery";
import { gql } from "graphql-request";
import {
  IGetAllWidget,
  IGetMessageByNoteIdArgs,
  IGetMessagesByNoteId,
  IGetNoteByNoteId,
  INote,
  INoteMessage
} from "./notesType";
import { queryArgsActions } from "../../../../store/slices/query-args/queryArgsSlice";

const messagesResultQuery = `{ data {
    totalCount
    pageSize
    pageNo 
    messages {
      id
      noteId
      type
      text
      archived
      isFavourite
      attachments
      replyId
    }
  }
  error }
  `;

const noteResultQuery = `data {
    id
    userId
    contentId
    contentType
    title
    isFavourite  
  }
  error `;

export const NotesAPI = createApi({
  reducerPath: "notesSlice",
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getWidgetByContentId: builder.query<IGetAllWidget, string>({
      query: (contentId) => {
        return {
          url: `/widget/all/${contentId}`,
          method: "GET"
        };
      },
      extraOptions: { service: "Widget" }
    }),
    getNoteByNoteId: builder.query<IGetNoteByNoteId, string>({
      query: (noteId) => ({
        url: "/graphql",
        body: gql`query getNoteByNoteId($noteId: String!) {
                getNotesByNoteId(noteId: $noteId) {
                  ${noteResultQuery}
                }
              }`,
        params: {
          variables: {
            noteId
          }
        }
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const resp = await queryFulfilled;
          if (resp.data.getNotesByNoteId.data.id) {
            dispatch(queryArgsActions.setNote(resp.data.getNotesByNoteId.data));
          }
        } catch (error) {
          // error while updating setNote
        }
      },
      extraOptions: { service: "Widget" }
    }),
    getMessagesByNoteId: builder.query<
      IGetMessagesByNoteId,
      IGetMessageByNoteIdArgs
    >({
      query: (variables) => ({
        url: "/graphql",
        body: gql`query GetMessagesByNoteId($noteId: String!, $pageNumber: Int!, $pageSize: Int!, $sortKey: String!, $sortOder: String!) {
                    getMessagesByNoteId(
                      noteId: $noteId
                      pageNumber: $pageNumber
                      pageSize: $pageSize
                      sortKey: $sortKey
                      sortOrder: $sortOder
                    )
                    ${messagesResultQuery}
                  }`,
        params: {
          variables
        }
      }),
      providesTags: (result, _, args) => {
        const tags = result?.getMessagesByNoteId?.data?.messages?.map(
          ({ id }) => {
            return { type: "Message", id };
          }
        );
        return [{ type: "MessageList", id: args.noteId }] as any;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return String(queryArgs.noteId);
      },
      merge(currentCacheData, responseData, _otherArgs) {
        if (_otherArgs.arg.pageNumber > 0) {
          const messageMap = new Map();
          [
            ...currentCacheData.getMessagesByNoteId.data.messages,
            ...responseData.getMessagesByNoteId.data.messages
          ].map((msg) => {
            if (!messageMap.has(msg.id)) {
              messageMap.set(msg.id, msg);
            }
          });
          const mergedResult = [...messageMap.values()];
          return {
            ...responseData,
            getMessagesByNoteId: {
              ...responseData.getMessagesByNoteId,
              data: {
                ...responseData.getMessagesByNoteId.data,
                messages: mergedResult
              }
            }
          };
        } else return responseData;
      },
      extraOptions: { service: "Widget" }
    }),
    upsertNote: builder.mutation<IBaseMutationResponse, INote>({
      query: (body): any => {
        return {
          url: "/note",
          method: "POST",
          body
        };
      },
      extraOptions: { service: "Widget" },
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          if (!patch?.id) {
            dispatch(
              queryArgsActions.setNote({
                ...patch,
                id: result?.data?.data?.id!
              })
            );
          }
        } catch {
          dispatch(queryArgsActions.setNote({}));
        }
      }
    }),
    upsertNoteMessage: builder.mutation<
      IBaseMutationResponse,
      Partial<INoteMessage>
    >({
      query: (body): any => {
        return {
          url: "/note/message",
          method: "POST",
          body: {
            ...body
          }
        };
      },
      extraOptions: { service: "Widget" }
      // invalidatesTags: (_result, _error, args) => {
      //   return [{ type: "MessageList", id: args?.noteId }] as any;
      // },
    }),
    deleteNoteMessage: builder.mutation<IBaseMutationResponse, INoteMessage>({
      query: ({ id }): any => {
        return {
          url: "/note/message/" + id,
          method: "DELETE"
        };
      },
      // invalidatesTags: (_result, _error, args) => {
      //   return [{ type: "MessageList", id: args.noteId }] as any;
      // },
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(queryArgsActions.setNextNotePages(0));
        } catch (error) {
          // console.log(error)
        }
      },
      extraOptions: { service: "Widget" }
    }),
    deleteNote: builder.query<IBaseMutationResponse, string>({
      query: (noteId): any => {
        return {
          url: "/note/" + noteId,
          method: "DELETE"
        };
      },
      extraOptions: { service: "Widget" }
    })
  })
});

export const {
  useGetNoteByNoteIdQuery,
  useGetWidgetByContentIdQuery,
  useLazyGetNoteByNoteIdQuery,
  useLazyGetWidgetByContentIdQuery,
  useUpsertNoteMutation,
  useGetMessagesByNoteIdQuery,
  useDeleteNoteMessageMutation,
  useLazyGetMessagesByNoteIdQuery,
  useUpsertNoteMessageMutation
} = NotesAPI;

export default NotesAPI;
